import {GlobalErrorProp} from './GlobalErrorprops';
import {t} from 'i18next';
import {errorHandler} from '@shared/constants/errorHandlers';

export const errorStatuses = {
  500: '500',
  400: '400',
  itf_140: 'itf_140',
  itf_141: 'itf_141',
};

// ── Module-level helpers (extracted to reduce getErrorObject cognitive complexity) ──

/** Returns 'info' modal title when message matches a known info-level error, otherwise 'error'. */
function computeErrorTitle(message: string | undefined): string {
  const isInfoMsg =
    !!(message?.includes(errorHandler?.notInActivity) ||
      message?.includes(errorHandler?.alreadyInShift));
  return isInfoMsg
    ? t('createFlow.common.modalTitles.info')
    : t('createFlow.common.modalTitles.error');
}

/** Builds the CTAs array for a repair-details-aware error modal. */
function computeCtas(isFromRepairDetails: boolean | undefined, navigateLabel: string, dismissLabel: string) {
  return isFromRepairDetails
    ? [{label: navigateLabel, type: 'NAVIGATE'}]
    : [{label: dismissLabel, type: 'DISMISS'}];
}

/** Computes the screen string for error modal routing. */
function computeScreen(isFromRepairDetails: boolean | undefined): string {
  return isFromRepairDetails ? 'repairDetails' : '';
}

/** Builds the error object for HTTP 500 responses. */
function build500ErrorObject(message: string | undefined, isFromRepairDetails: boolean | undefined) {
  return {
    subTitle: message || 'common.networkError.unableTitle',
    title: computeErrorTitle(message),
    message: '',
    ctas: computeCtas(isFromRepairDetails, 'common.networkError.unableCta', 'common.networkError.unableCtaDismiss'),
    screen: computeScreen(isFromRepairDetails),
    noTranslation: false,
  };
}

/** Builds the error object for HTTP 400 responses. */
function build400ErrorObject(message: string | undefined, isFromRepairDetails: boolean | undefined) {
  return {
    subTitle: message || 'Bad Request',
    title: computeErrorTitle(message),
    message: '',
    ctas: computeCtas(isFromRepairDetails, 'Back to Repairs', 'OK'),
    screen: computeScreen(isFromRepairDetails),
    noTranslation: false,
  };
}

/** Builds the error object for all other (default) error responses. */
function buildDefaultErrorObject(message: string | undefined, isFromRepairDetails: boolean | undefined) {
  return {
    subTitle: message ?? '',
    title: '',
    message: message ? '' : '', // NOSONAR
    ctas: computeCtas(isFromRepairDetails, 'common.networkError.unableCta', 'common.networkError.unableCtaDismiss'),
    screen: computeScreen(isFromRepairDetails),
    noTranslation: false,
  };
}

/**
 * Returns a formatted error object for modal display.
 * @param status - HTTP status code
 * @param errorCode - Specific API error code
 * @returns object | null
 */
export const getErrorObject = ({
  message,
  status,
  isFromRepairDetails,
}: GlobalErrorProp) => {
  switch (status) {
    case errorStatuses[500]:
      return build500ErrorObject(message, isFromRepairDetails);
    case errorStatuses[400]:
      return build400ErrorObject(message, isFromRepairDetails);
    default:
      return buildDefaultErrorObject(message, isFromRepairDetails);
  }
};
